import { Button } from '@creditornot/cb-button';
import { MagnifyingGlass } from '@creditornot/cb-icons';
import { cbSemanticCssVars, elevationCss } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Empty } from '@creditornot/cb-placeholders';
import React, { useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import Loading from 'core/Loading';
import { DiscountedItem } from 'modules/campaigns/hooks/useDiscountedItems';
import { SingleItemDiscountItemFormValues } from 'modules/campaigns/types';

import DiscountedItemListRowRenderer from './DiscountedItemListRowRenderer';

type DiscountedItemSelectorModalProps = {
  discountedItems: DiscountedItem[];
  isLoadingMenuData: boolean;
  onClose: () => void;
  onUpdate: () => void;
};

const ModalContainer = styled.div`
  background-color: ${cbSemanticCssVars.surfaceMain};
  min-width: 800px;
  border-radius: 8px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: auto;
    width: 98vw;
  }
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`;

const StyledDiscountedItemsContainer = styled.div`
  height: 300px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: calc(100vh - 250px);
  }
`;

const SearchContainer = styled.div`
  display: flex;
  height: 68px;
  padding: 16px 24px;
  justify-content: space-between;
  align-items: center;

  ${elevationCss(2)}
`;

const EmptyListContainer = styled.div`
  margin-block: 24px;
`;

const StyledLoading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled(Input).attrs({ icon: <MagnifyingGlass />, stretch: false })``;

export const DiscountedItemSelectorModal = ({
  discountedItems,
  isLoadingMenuData,
  onClose,
  onUpdate,
}: DiscountedItemSelectorModalProps) => {
  const [searchValue, setSearchValue] = useState('');
  const { formatMessage } = useIntl();
  const {
    input: { value: selectedItemId },
  } = useField<SingleItemDiscountItemFormValues['itemId']>('itemId');
  const filteredDiscountedItems = useMemo(() => {
    return discountedItems.filter(option => option.name.toLowerCase().includes(searchValue.toLowerCase()));
  }, [discountedItems, searchValue]);

  return (
    <ModalContainer>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="campaigns.form.item.select-item-discount" />
      </ModalHeader>
      {isLoadingMenuData ? (
        <StyledLoading>
          <Loading />
        </StyledLoading>
      ) : (
        <>
          <SearchContainer>
            <Typography.Body3>
              <FormattedMessage id="campaigns.form.item.showing-all" />
            </Typography.Body3>

            <SearchInput
              onClearClick={() => setSearchValue('')}
              onChange={e => setSearchValue(e.target.value)}
              value={searchValue}
              placeholder={formatMessage({ id: 'campaigns.form.item.search-for-item' })}
            />
          </SearchContainer>
          <StyledModalContent>
            <StyledDiscountedItemsContainer>
              <AutoSizer disableWidth>
                {({ height }) => (
                  <FixedSizeList
                    width="100%"
                    height={height}
                    itemData={filteredDiscountedItems}
                    itemCount={filteredDiscountedItems.length}
                    itemSize={60}
                    itemKey={(index, data) => data[index].id}
                    overscanCount={5}
                  >
                    {DiscountedItemListRowRenderer}
                  </FixedSizeList>
                )}
              </AutoSizer>
              {filteredDiscountedItems.length === 0 && (
                <EmptyListContainer>
                  <Empty title={formatMessage({ id: 'campaigns.form.item.no-item' })} />
                </EmptyListContainer>
              )}
            </StyledDiscountedItemsContainer>
          </StyledModalContent>
          <StyledModalFooter>
            <Button variant="lightRed" size="small" onClick={onClose}>
              <FormattedMessage id="campaigns.form.item.cancel" />
            </Button>
            <Button variant="blue" size="small" onClick={onUpdate} disabled={!selectedItemId}>
              <FormattedMessage id="campaigns.form.item.select-item" />
            </Button>
          </StyledModalFooter>
        </>
      )}
    </ModalContainer>
  );
};
